import { NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  Store, ShieldAlert, DollarSign, LogOut, CloudLightning, Menu, X,
  CheckCircle2, History, ChevronDown, ScrollText,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import client from '../../api/client.js'
import { clsx } from 'clsx'
import { useAuth } from '../../auth/AuthContext.jsx'

// The Actions umbrella collapses three related routes (Active / Done / Audit)
// into one dropdown menu so the top nav stays at 4 entries — see the chat
// thread for the TECH-24 review where this UX was agreed.
const ACTIONS_MENU = [
  { to: '/actions',       label: 'Active', icon: ShieldAlert,  exact: true },
  { to: '/actions/done',  label: 'Done',   icon: CheckCircle2 },
  // ``cerebiOnly`` mirrors the Logs nav-link gate — Audit is operational
  // / forensic and shouldn't be visible to customer-side users. The /api/audit
  // endpoints are also 403-gated on the backend (audit_router), so even a
  // bookmark on the URL won't return data for a non-cerebi account.
  { to: '/audit',         label: 'Audit',  icon: History, cerebiOnly: true },
]

const NAV_LINKS = [
  { to: '/', label: 'Risk Prediction', icon: CloudLightning },
  // Actions ▾ — handled separately (dropdown), not a plain NavLink.
  { to: '/stores', label: 'Stores', icon: Store },
  { to: '/financials', label: 'Financials', icon: DollarSign },
]

function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const { data } = await client.get('/health')
      return data
    },
    refetchInterval: 30_000,  // every 30s
    retry: 1,
  })
}

export default function Navbar() {
  const location = useLocation()
  const { data: health } = useHealth()
  const { user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const isCerebi = user?.email?.endsWith('@cerebi.ai')
  const hamburgerRef = useRef(null)

  const duckdbOk = health?.duckdb_ok ?? null
  const ollamaOk = health?.ollama_ok ?? null

  // Close on route change so navigating from the drawer dismisses it.
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 h-16"
      style={{
        background: 'rgba(10, 15, 30, 0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        boxShadow: '0 4px 32px rgba(0,0,0,0.3)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between gap-6">
        {/* Logo / Brand */}
        <NavLink to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <motion.img
            src="/logo.png"
            alt="AEO × CereBI logo"
            className="h-8 w-auto rounded-lg"
            whileHover={{ scale: 1.08 }}
            transition={{ type: 'spring', stiffness: 400 }}
          />
        </NavLink>

        {/* Navigation links — md and up. Risk Prediction · Actions ▾ ·
            Stores · Financials. The Actions dropdown is rendered inline
            to keep the order stable. */}
        <div className="hidden md:flex items-center gap-1">
          <PlainNavLink {...NAV_LINKS[0]} location={location} />
          <ActionsDropdown location={location} isCerebi={isCerebi} />
          <PlainNavLink {...NAV_LINKS[1]} location={location} />
          <PlainNavLink {...NAV_LINKS[2]} location={location} />
        </div>

        {/* Status indicators + user / hamburger */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <StatusDot label="DB" ok={duckdbOk} />
          <StatusDot label="AI" ok={ollamaOk} />
          <div className="hidden md:block">
            <UserMenu />
          </div>
          <button
            ref={hamburgerRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="md:hidden p-2 rounded-lg text-slate-300 hover:bg-white/5 transition-colors"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <MobileDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        returnFocusRef={hamburgerRef}
      />
    </nav>
  )
}

function PlainNavLink({ to, label, icon: Icon, exact, location }) {
  const isActive = to === '/'
    ? location.pathname === '/'
    : exact
      ? location.pathname === to
      : location.pathname.startsWith(to)

  return (
    <NavLink to={to} end={exact} className="relative">
      <motion.div
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
          isActive
            ? 'text-cyan-300'
            : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Icon size={15} />
        <span>{label}</span>
        {isActive && (
          <motion.div
            layoutId="active-nav"
            className="absolute inset-0 rounded-lg"
            style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
            }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
        )}
      </motion.div>
    </NavLink>
  )
}


function ActionsDropdown({ location, isCerebi }) {
  // Split-button interaction model:
  //   - Click anywhere on the trigger → navigate to /actions (Active).
  //   - Hover the chevron (>) → reveal a dropdown showing only the OTHER
  //     destinations (Done, Audit). Active isn't repeated in the menu
  //     because clicking the trigger already takes you there.
  //   - 180ms close-delay so the cursor can travel from chevron to menu
  //     across the visual gap without snapping the menu shut.
  //   - Escape / click-outside / route change still close.
  const [open, setOpen] = useState(false)
  const closeTimerRef = useRef(null)
  const containerRef = useRef(null)

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const handleEnter = () => {
    cancelClose()
    setOpen(true)
  }

  const handleLeave = () => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => setOpen(false), 180)
  }

  // Close on route change.
  useEffect(() => { setOpen(false) }, [location.pathname])

  // Escape closes; click-outside catches touch users who tapped to open.
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    function onPointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  // Clear any pending close timer on unmount.
  useEffect(() => () => cancelClose(), [])

  const isAnyActionsRouteActive = ACTIONS_MENU.some((m) =>
    m.exact ? location.pathname === m.to : location.pathname.startsWith(m.to)
  )

  // Active is the trigger's own destination — don't repeat it in the menu.
  const visibleMenu = ACTIONS_MENU
    .filter((m) => m.to !== '/actions')
    .filter((m) => !m.cerebiOnly || isCerebi)

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseLeave={handleLeave}
    >
      {/* The whole trigger navigates to /actions (Active) on click.
          Hovering specifically the chevron area opens the dropdown of the
          other destinations. */}
      <NavLink
        to="/actions"
        end
        aria-haspopup="menu"
        aria-expanded={open}
        className={clsx(
          'relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
          isAnyActionsRouteActive
            ? 'text-cyan-300'
            : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
        )}
      >
        <ShieldAlert size={15} />
        <span>Actions</span>
        {/* Hover hotspot for the dropdown. Click still navigates because
            this is inside the NavLink — that's intentional and matches the
            "click = always go to Active" model. */}
        <span
          onMouseEnter={handleEnter}
          className="-mr-1 px-1 inline-flex items-center"
          aria-label="More actions menu"
        >
          <ChevronDown
            size={13}
            className={clsx('transition-transform', open && 'rotate-180')}
          />
        </span>
        {isAnyActionsRouteActive && (
          <motion.div
            layoutId="active-nav"
            className="absolute inset-0 rounded-lg pointer-events-none"
            style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
            }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
        )}
      </NavLink>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            // ``pt-1.5`` instead of ``mt-1.5`` keeps the menu visually inset
            // 6px from the trigger but the wrapper hit-area continuous, so
            // cursor travel from trigger → menu doesn't fire onMouseLeave.
            className="absolute left-0 top-full pt-1.5 min-w-[180px] z-50"
          >
            <div
              className="py-1 rounded-lg border border-white/10 shadow-2xl"
              style={{
                background: 'rgba(13, 20, 40, 0.96)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
            >
              {visibleMenu.map(({ to, label, icon: Icon, exact }) => {
                const isActive = exact
                  ? location.pathname === to
                  : location.pathname.startsWith(to)
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={exact}
                    role="menuitem"
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'text-cyan-300 bg-cyan-500/10'
                        : 'text-slate-300 hover:text-slate-100 hover:bg-white/5'
                    )}
                  >
                    <Icon size={14} className={isActive ? 'text-cyan-400' : 'text-slate-500'} />
                    {label}
                  </NavLink>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


function UserMenu() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)
  const isCerebi = user?.email?.endsWith('@cerebi.ai')

  useEffect(() => { setOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    function onPointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  if (!user) return null

  const avatar = user.picture
    ? <img src={user.picture} alt={user.name} className="mp-mask w-7 h-7 rounded-full ring-1 ring-white/10" />
    : <div className="mp-mask w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[11px] font-bold text-cyan-400">
        {user.name?.[0]?.toUpperCase() ?? '?'}
      </div>

  return (
    <div ref={menuRef} className="relative flex items-center gap-2 pl-3 border-l border-white/10">
      {isCerebi ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Open user menu"
          className="rounded-full transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
        >
          {avatar}
        </button>
      ) : avatar}
      <span className="mp-mask text-xs text-slate-400 hidden xl:inline max-w-[160px] truncate">{user.email}</span>
      <button
        onClick={logout}
        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        title="Sign out"
      >
        <LogOut size={13} />
      </button>
      <AnimatePresence>
        {isCerebi && open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            className="absolute right-0 top-full pt-2 min-w-[180px] z-50"
          >
            <div
              className="py-1 rounded-lg border border-white/10 shadow-2xl"
              style={{
                background: 'rgba(13, 20, 40, 0.96)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
            >
              <NavLink
                to="/logs"
                role="menuitem"
                className={({ isActive }) => clsx(
                  'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'text-cyan-300 bg-cyan-500/10'
                    : 'text-slate-300 hover:text-slate-100 hover:bg-white/5'
                )}
              >
                <ScrollText size={14} className="text-slate-500" />
                Logs
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function StatusDot({ label, ok }) {
  const isLoading = ok === null
  return (
    <div className="flex items-center gap-1.5">
      <motion.div
        className={clsx(
          'w-2 h-2 rounded-full',
          isLoading ? 'bg-slate-500' :
          ok ? 'bg-emerald-400' : 'bg-red-400'
        )}
        animate={ok && !isLoading ? { scale: [1, 1.3, 1] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
        {label}
      </span>
    </div>
  )
}

function MobileDrawer({ open, onClose, returnFocusRef }) {
  const { user, logout } = useAuth()
  // Mirrors the same gate in Navbar() — pre-existing in the merged code but
  // wasn't actually in scope inside this component, which would have thrown
  // ReferenceError once the drawer opened.
  const isCerebi = user?.email?.endsWith('@cerebi.ai')
  const panelRef = useRef(null)
  const firstFocusableRef = useRef(null)
  const mobileUserMenuRef = useRef(null)
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false)

  useEffect(() => {
    if (!open) setAvatarMenuOpen(false)
  }, [open])

  useEffect(() => {
    if (!avatarMenuOpen) return
    function onPointerDown(e) {
      if (mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(e.target)) {
        setAvatarMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [avatarMenuOpen])

  // Focus management + Escape + Tab trap.
  useEffect(() => {
    if (!open) return

    // Defer the focus call until after the drawer mounts.
    const focusTimer = setTimeout(() => { firstFocusableRef.current?.focus() }, 0)

    function handleKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => {
      clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKey)
      try { returnFocusRef?.current?.focus?.() } catch {}
    }
  }, [open, onClose, returnFocusRef])

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — md:hidden because the drawer itself only opens at narrow widths */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="md:hidden fixed inset-0 top-16 bg-black/40 z-40"
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Panel */}
          <motion.div
            id="mobile-nav"
            ref={panelRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="md:hidden fixed top-16 left-0 right-0 z-50 border-b border-white/10"
            style={{
              background: 'rgba(10, 15, 30, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {/* On mobile we expand the Actions umbrella back out — drawers
                  are vertical lists, so a nested popover would be hostile.
                  cerebiOnly entries (Audit) are filtered so the same gate
                  applies on touch devices. */}
              {[
                NAV_LINKS[0],          // Risk Prediction
                ...ACTIONS_MENU.filter((m) => !m.cerebiOnly || isCerebi),
                NAV_LINKS[1],          // Stores
                NAV_LINKS[2],          // Financials
              ].map(({ to, label, icon: Icon, exact }, idx) => (
                <NavLink
                  key={to}
                  to={to}
                  end={exact}
                  ref={idx === 0 ? firstFocusableRef : undefined}
                  className={({ isActive }) => clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/20'
                      : 'text-slate-300 hover:text-slate-100 hover:bg-white/5'
                  )}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              ))}

              {user && (
                <div ref={mobileUserMenuRef} className="relative mt-2 pt-3 border-t border-white/10 flex items-center gap-2.5 px-2">
                  {isCerebi ? (
                    <button
                      type="button"
                      onClick={() => setAvatarMenuOpen((v) => !v)}
                      aria-haspopup="menu"
                      aria-expanded={avatarMenuOpen}
                      aria-label="Open user menu"
                      className="rounded-full transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
                    >
                      {user.picture
                        ? <img src={user.picture} alt={user.name} className="mp-mask w-7 h-7 rounded-full ring-1 ring-white/10" />
                        : <div className="mp-mask w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[11px] font-bold text-cyan-400">
                            {user.name?.[0]?.toUpperCase() ?? '?'}
                          </div>
                      }
                    </button>
                  ) : user.picture
                    ? <img src={user.picture} alt={user.name} className="mp-mask w-7 h-7 rounded-full ring-1 ring-white/10" />
                    : <div className="mp-mask w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[11px] font-bold text-cyan-400">
                        {user.name?.[0]?.toUpperCase() ?? '?'}
                      </div>
                  }
                  <div className="mp-mask flex-1 min-w-0 text-xs text-slate-300 truncate">{user.email}</div>
                  <button
                    onClick={logout}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Sign out"
                  >
                    <LogOut size={13} />
                  </button>
                  <AnimatePresence>
                    {isCerebi && avatarMenuOpen && (
                      <motion.div
                        role="menu"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.14, ease: 'easeOut' }}
                        className="absolute left-2 bottom-full mb-2 min-w-[180px] z-50"
                      >
                        <div
                          className="py-1 rounded-lg border border-white/10 shadow-2xl"
                          style={{
                            background: 'rgba(13, 20, 40, 0.96)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                          }}
                        >
                          <NavLink
                            to="/logs"
                            role="menuitem"
                            className={({ isActive }) => clsx(
                              'flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                              isActive
                                ? 'text-cyan-300 bg-cyan-500/10'
                                : 'text-slate-300 hover:text-slate-100 hover:bg-white/5'
                            )}
                          >
                            <ScrollText size={14} className="text-slate-500" />
                            Logs
                          </NavLink>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
